import { useEffect, useState } from "react";
import { Trophy, Gift, UserPlus, Medal, Zap, Timer } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { shareRunCard } from "@/lib/giftcard";
import { WinnerCard, ShareRunButton } from "@/components/GiftCardExtras";

const base = `${process.env.PUBLIC_URL || ""}/game-assets`;
const MEDAL = ["#FFD54F", "#cfd8dc", "#d18f4b"];
export const fmtTime = (ms) => { const s = Math.max(0, Math.round((ms || 0) / 100) / 10); return `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`; };

// Sponsored Gift Card Challenge board (template-driven, see /api/giftcard/*):
// one level, fixed hard difficulty, rolling window; #1 by points wins the card.
export function GiftCardBoard({ challenge, onAuth, onPlay }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!challenge?.id) return;
    api.giftcardBoard(challenge.id).then((r) => setData(r.data)).catch(() => setData({ top: [], players: 0 }));
  }, [challenge?.id, user?.id]);

  const ch = data?.challenge || challenge;
  const prize = lang === "es" ? ch.prize : ch.prize_en || ch.prize;
  const rows = data?.top || [];
  const logo = ch.logo ? `${base}/${ch.logo}` : `${base}/nicabros-logo.webp`;

  return (
    <div data-testid={`giftcard-board-${ch.id}`} className="relative z-10 w-full pixel-card p-0 overflow-hidden" style={{ background: "linear-gradient(160deg,#12294f 0%,#0a1c38 100%)" }}>
      <div className="px-4 pt-4 flex items-center gap-3">
        <img src={logo} alt={ch.sponsor || ch.name} className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-xl bg-white shrink-0" data-testid="giftcard-logo" />
        <div className="min-w-0">
          <p className="font-pixel text-[11px] sm:text-sm text-[#FFD54F] pixel-stroke flex items-center gap-1.5" data-testid="giftcard-title">
            <Trophy size={14} strokeWidth={2.5} /> {t("etBoardTitle")}
          </p>
          <p className="font-vt text-base text-white leading-tight mt-0.5">{ch.sponsor || ch.name}</p>
          <p className="font-vt text-sm text-white/70 leading-tight">{t("etBoardSub")(data?.days || ch.days || 30)}</p>
        </div>
      </div>

      <div className="mx-4 mt-3 rounded-xl bg-white/10 ring-1 ring-white/20 px-3 py-2 flex items-start gap-2" data-testid="giftcard-prize">
        <Gift size={16} strokeWidth={2.5} className="text-[#FFD54F] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-pixel text-[8px] text-white/60">{t("etBoardPrize")}</p>
          <p className="font-vt text-base sm:text-lg text-white leading-tight">{prize}</p>
        </div>
      </div>

      <div className="mx-4 mt-2 rounded-xl bg-[#E91E63]/20 ring-1 ring-[#E91E63]/50 px-3 py-2" data-testid="giftcard-rules">
        <p className="font-pixel text-[8px] text-[#ff80ab] flex items-center gap-1.5"><Zap size={11} strokeWidth={2.5} /> {t("gcRules")}</p>
        <p className="font-vt text-sm text-white/70 leading-tight mt-1">{t("gcFormula")(ch.beer_pts ?? 100, ch.time_pts ?? 25, ch.death_penalty ?? 500)}</p>
      </div>

      <div className="px-4 pt-3 pb-4">
        {!data ? (
          <p className="font-vt text-base text-white/60">…</p>
        ) : rows.length === 0 ? (
          <p className="font-vt text-base text-white/70 text-center py-2" data-testid="giftcard-empty">{t("etBoardEmpty")}</p>
        ) : (
          <ol className="space-y-1" data-testid="giftcard-list">
            {rows.slice(0, 10).map((r) => {
              const mine = user && r.username === user.username;
              return (
                <li key={r.rank} data-testid={`giftcard-row-${r.rank}`} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 ${mine ? "bg-[#4CAF50]/25 ring-1 ring-[#4CAF50]/60" : "bg-white/5"}`}>
                  <span className="w-6 shrink-0 text-center font-pixel text-[10px]" style={{ color: MEDAL[r.rank - 1] || "rgba(255,255,255,0.6)" }}>
                    {r.rank <= 3 ? <Medal size={15} strokeWidth={2.5} className="inline" /> : r.rank}
                  </span>
                  <span className="flex-1 min-w-0 truncate font-vt text-base text-white">{r.username}{mine ? ` (${t("etBoardYou")})` : ""}</span>
                  <span className="hidden sm:inline font-vt text-xs text-white/55 whitespace-nowrap"><Timer size={10} className="inline -mt-0.5" /> {fmtTime(r.time_ms)} · 🍺{r.beers} · ✖{r.deaths}</span>
                  <span className="font-pixel text-[10px] text-[#FFD54F]">{r.points.toLocaleString()}</span>
                </li>
              );
            })}
          </ol>
        )}
        {data && <p className="mt-2 font-vt text-sm text-white/50 text-center">{t("etBoardPlayers")(data.players || 0)}</p>}
        {data?.me && (
          <>
            <p className="mt-1 font-vt text-base text-[#8BC34A] text-center" data-testid="giftcard-me">{t("etBoardYourRank")(data.me.rank, data.me.points.toLocaleString())}</p>
            <ShareRunButton className="mt-2 w-full" onShare={() => shareRunCard({ challenge: ch, run: data.me, username: user?.username, rank: data.me.rank, es: lang === "es" })} />
          </>
        )}
        {data?.my_award && <WinnerCard award={data.my_award} challenge={ch} />}
        {onPlay && (
          <button data-testid={`giftcard-play-btn-${ch.id}`} onClick={() => onPlay(ch)} className="mt-3 w-full pixel-btn bg-[#E91E63] text-white px-4 py-3 text-[10px] flex items-center justify-center gap-1.5">
            <Zap size={14} strokeWidth={2.5} /> {t("gcPlay")}
          </button>
        )}
        {!user && onAuth && (
          <div className="mt-2">
            <button data-testid={`giftcard-register-btn-${ch.id}`} onClick={() => onAuth("register")} className="w-full pixel-btn bg-[#FFD54F] text-[#0a1c38] px-4 py-2.5 text-[10px] flex items-center justify-center gap-1.5">
              <UserPlus size={14} strokeWidth={2.5} /> {t("etBoardRegister")}
            </button>
            <p className="mt-1.5 font-vt text-sm text-white/55 text-center leading-tight">{t("etBoardRegisterHint")}</p>
          </div>
        )}
        <p className="mt-2 font-vt text-xs text-white/40 text-center">{t("etBoardPoweredBy")}</p>
      </div>
    </div>
  );
}

export default GiftCardBoard;
